import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ConvertStringToReact } from '../../components/ConvertStringToReact';
import { AlertBanner } from '../../components/AlertBanner';
import { Checkbox } from '../../components/Checkbox';
import { gtagEnvelope } from '../../util/gtagEnvelope';
import styles from './Trial.module.css';
import { Formik, Form, Field, FormikProps } from 'formik';
import { InfoBanner } from '~/common/components/InfoBanner';
import { SendAsyncPostObject, TrialViewProps, ZoomInfoResponse } from '~/common/types';
import { BusinessInput } from './BusinessInput';
import { validateEmail } from '~/common/util/customValidation';
import { businessEmailCheck } from '~/common/util/businessEmail';
import { BusinessBuyInput } from './BusinessBuyInput';
import { UserIcon } from '~/common/components/SvgIcons';
import { MSPInput } from './MSPInput';
import { FormikEffect } from '~/common/components/FormikEffect';
import { handleStandardTrialSubmission } from '~/common/util/submissions/trialSubmission';
import { handleCompanyTrialSubmission } from '~/common/util/submissions/companySubmission';
import { getErrorMessage } from '~/common/util/getErrorMessage';
import { handleMSPTrialSubmission } from '~/common/util/submissions/mspSubmission';
import { EmailOnlyInput } from './EmailOnlyInput';
import { FORM_TYPES, MODAL_TYPES, MODIFIER_TYPE, TRIAL_TYPES } from '~/common/consts';
import { handleTeamsTrialSubmission } from '~/common/util/submissions/teamsSubmission';
import { handlePremiumTrialSubmission } from '~/common/util/submissions/premiumSubmission';
import { handleFamiliesTrialSubmission } from '~/common/util/submissions/familiesSubmission';
import { emailCheck } from '~/common/util/emailCheck';
import { postSegmentService, postTrialInterestBoomiService } from '~/common/services/trackingServices';
import DOMPurify from 'dompurify';
import { getEventName, getEventReason } from '~/common/util/getExperimentVersion';
import { TrialButtonSwitch } from './TrialButtonSwitch';
import { useAppStore } from '~/common/store/appStore';
import { useShallow } from 'zustand/react/shallow';
import { isModalModifier } from '~/common/util/isModal';
import { getZoomInfoData } from '~/common/services/sitecoreServices';
import { getWebEnrichedFields } from '~/common/util/getWebEnrichedFields';
import { useLatestAbortSignal } from '~/common/hooks/useLatestAbortSignal';

export const TrialView = (props: TrialViewProps) => {
  const {
    initialValues,
    directbuy,
    formError,
    formErrorCode,
    data,
    trialAllowed,
    trackError,
    setErrorMessage,
    setErrorCode,
    existingUserEmail,
    is2Step,
    handleCallback,
    modalContent,
    startStep,
    onBackOverride,
    isCanadaUser,
    initialZoomInfoData,
  } = props;
  const { modifier, trialType, formType, isCompany, trialLength, businessMax } = useAppStore(
    useShallow((state) => ({
      modifier: state.modifier,
      trialType: state.trialType,
      formType: state.formType,
      isCompany: state.isCompany,
      trialLength: state.trialLength,
      businessMax: state.businessMax,
    }))
  );

  //local state
  const [showHint, setShowHint] = useState<boolean>(false);
  const [formSubmitting, setFormSubmitting] = useState<boolean>(false);
  const [contactPermission, setContactPermission] = useState<boolean>(true);
  const [isBannerClosed, setIsBannerClosed] = useState<boolean>(false);
  const [marketingOptOutSeen, setMarketingOptOutSeen] = useState<boolean>(false);
  const [displayUnsubscribeCheckbox, setDisplayUnsubscribeCheckbox] = useState<boolean>(false);
  const [existingAccount, setExistingAccount] = useState<boolean>(false);
  const [mistypedEmail, setMistypedEmail] = useState<string>('');
  const [step, setStep] = useState<number>(startStep ?? 1);
  const [zoomInfoData, setZoomInfoData] = useState<ZoomInfoResponse | null>(initialZoomInfoData ?? null);

  const formikRef = useRef<FormikProps<SendAsyncPostObject>>(null);
  const nextEmailTypeCheckSignal = useLatestAbortSignal();
  const nextStepCheckSignal = useLatestAbortSignal();
  const nextSubmitSignal = useLatestAbortSignal();

  useEffect(() => {
    if (existingAccount || mistypedEmail) {
      formikRef.current?.setFieldError('email', 'error');
    }
  }, [existingAccount, mistypedEmail]);

  useEffect(() => {
    setContactPermission(!isCanadaUser);
  }, [isCanadaUser]);

  const setClosedBanner = (value: boolean) => {
    if (value !== isBannerClosed) {
      setIsBannerClosed(value);
    }
  };

  const trackErrorBlur = useCallback(
    (id: string, validation: (value: string | number) => string | boolean, value: string) => {
      const trimmed = value ? value.trim() : value;
      formikRef.current?.setFieldValue(id, trimmed);
      formikRef.current?.setFieldError(id, undefined);
      const isInvalid = validation(trimmed);
      setTimeout(() => {
        formikRef.current?.setFieldTouched(id, true);
        if (isInvalid) {
          formikRef.current?.setFieldError(id, 'error');
          gtagEnvelope('event', 'trial_page_error', {
            error_type: 'form_field_error',
            error_field: id,
            validation_failure_reason: isInvalid,
          });
        } else {
          gtagEnvelope('event', 'trial_field_completion', {
            form_field: id,
          });
        }
      }, 300);
      if (isInvalid) {
        return false;
      }
    },
    []
  );

  const validateBusinessEmail = useCallback(
    async (email: string) => {
      const signal = nextEmailTypeCheckSignal();
      setExistingAccount(false);
      formikRef.current?.setFieldTouched('email', true);
      setShowHint(false);
      if (zoomInfoData) {
        setZoomInfoData(null);
      }
      const invalid = validateEmail(email);
      if (invalid) {
        formikRef.current?.setFieldError('email', 'error');
        return false;
      }
      const business = await businessEmailCheck(email.toLowerCase(), signal);
      if (signal.aborted) return false;
      if (!business) {
        setShowHint(true);
      }
      return false;
    },
    [zoomInfoData]
  );

  const setOptOutOfEmails = () => {
    gtagEnvelope('event', 'cta_click', {
      cta_name: 'Email opt-out',
      cta_position: 'Trial Form',
    });
    setContactPermission(!contactPermission);
  };

  const unsubscribeClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.tagName === 'SPAN') {
      setMarketingOptOutSeen(true);
      gtagEnvelope('event', 'cta_click', {
        cta_name: 'Unsubscribe',
        cta_position: 'Trial Form',
      });

      if (isCanadaUser) {
        setContactPermission(false);
      } else {
        setDisplayUnsubscribeCheckbox(!displayUnsubscribeCheckbox);
      }
    }
  };

  const handleSub = async (
    values: SendAsyncPostObject,
    zoomEnrichedFields?: string,
    zoomContactToken?: string | null
  ) => {
    const signal = nextSubmitSignal();
    try {
      setFormSubmitting(true);
      setIsBannerClosed(false);
      setErrorMessage(null);
      setErrorCode(null);

      const webEnrichedFields = zoomEnrichedFields ?? (zoomInfoData ? getWebEnrichedFields(zoomInfoData) : undefined);
      const contactToken = zoomContactToken ?? zoomInfoData?.contactToken ?? undefined;

      let submitResponse;

      if (isCompany) {
        submitResponse = await handleCompanyTrialSubmission({
          data,
        });
      } else if (trialType === TRIAL_TYPES.MSP) {
        submitResponse = await handleMSPTrialSubmission({
          data,
          values,
          marketingOptOutSeen,
          contactPermission,
          signal,
        });
      } else if (trialType === TRIAL_TYPES.TEAMS) {
        submitResponse = await handleTeamsTrialSubmission({
          data,
          existingUserEmail,
          values,
          marketingOptOutSeen,
          contactPermission,
          signal,
        });
      } else if (trialType === TRIAL_TYPES.PREMIUM) {
        submitResponse = await handlePremiumTrialSubmission({
          data,
          values,
          marketingOptOutSeen,
          contactPermission,
          signal,
        });
        if (signal.aborted) return;
        if (submitResponse && submitResponse.code === 1002 && submitResponse.correctedEmail) {
          setMistypedEmail(submitResponse.correctedEmail);
        }
      } else if (trialType === TRIAL_TYPES.FAMILIES) {
        submitResponse = await handleFamiliesTrialSubmission({
          data,
          existingUserEmail,
          values,
          marketingOptOutSeen,
          contactPermission,
          signal,
        });
        if (signal.aborted) return;
        if (submitResponse && submitResponse.code === 1002 && submitResponse.correctedEmail) {
          setMistypedEmail(submitResponse.correctedEmail);
        }
      } else {
        submitResponse = await handleStandardTrialSubmission({
          data,
          existingUserEmail,
          values,
          marketingOptOutSeen,
          contactPermission,
          webEnrichedFields,
          contactToken,
          signal,
        });
      }
      if (signal.aborted) return;
      if (submitResponse && !submitResponse.success) {
        if (submitResponse.code === 2006) {
          setExistingAccount(true);
        }

        setErrorMessage(getErrorMessage(data, submitResponse.code, existingUserEmail) || data.globalError);
        trackError({
          code: submitResponse.code,
          message: submitResponse.errorMessage || getErrorMessage(data, submitResponse.code) || data.globalError,
          step,
        });
        setFormSubmitting(false);
      }
    } catch (error) {
      if (signal.aborted) return;
      setErrorMessage(data.globalError);
      trackError({
        code: '500',
        message: `Trial submission: ${error.message}`,
        runtime: true,
      });
      setFormSubmitting(false);
    }
  };

  useEffect(() => {
    if (startStep !== 2 || !initialZoomInfoData || !formikRef.current) {
      return;
    }
    const res = initialZoomInfoData;
    if (res.isComplete) {
      const zoomInfoValues: SendAsyncPostObject = {
        ...formikRef.current.values,
        fullName: res.hasFirstName && res.hasLastName ? null : formikRef.current.values.fullName,
        title: res.hasJobTitle ? null : formikRef.current.values.title,
        company: res.hasCompanyName ? null : formikRef.current.values.company,
        employees: res.hasCompanyEmployeeCount ? null : formikRef.current.values.employees,
      };
      handleSub(zoomInfoValues, getWebEnrichedFields(res), res.contactToken);
    }
  }, []);

  const goToNextStep = async () => {
    const determinedGtmCtaPosition = (): string => {
      if (modifier === MODIFIER_TYPE.HERO && businessMax) {
        return 'Business Max Hero Trial Form';
      }
      return 'Trial Form';
    };
    gtagEnvelope('event', 'cta_click', {
      cta_name: 'Next step',
      cta_position: determinedGtmCtaPosition(),
    });
    const signal = nextStepCheckSignal();
    setFormSubmitting(true);
    setErrorMessage(null);
    setIsBannerClosed(false);
    try {
      if (!formikRef.current?.values?.email) {
        return;
      }
      await postSegmentService({
        event: `${getEventName(true)}_trial_form_click`,
        properties: {
          reason: getEventReason(),
          journey_step: 'Step 1',
        },
      });
      const formEmail = DOMPurify.sanitize(formikRef.current.values.email.toString());
      const bossInvalid = await emailCheck(formEmail, signal);
      if (signal.aborted) return;

      if (!bossInvalid.success) {
        if (bossInvalid.code === 2006) {
          setExistingAccount(true);
        }
        setErrorMessage(getErrorMessage(data, bossInvalid.code) || data.globalError);
        trackError({
          code: bossInvalid.code,
          message: bossInvalid.errorMessage || getErrorMessage(data, bossInvalid.code) || data.globalError,
        });
      } else {
        setExistingAccount(false);
        const isZoomInfoWorkflow =
          window.LPFeatureFlags &&
          window.LPFeatureFlags.enableZoomInfoEnrichApi &&
          (trialType === TRIAL_TYPES.BUSINESS || businessMax);
        if (isZoomInfoWorkflow) {
          const res = await getZoomInfoData(formEmail, signal);
          if (signal.aborted) return;
          if (res?.isComplete) {
            const zoomInfoValues: SendAsyncPostObject = {
              ...formikRef.current?.values,
              fullName: res.hasFirstName && res.hasLastName ? null : formikRef.current?.values.fullName,
              title: res.hasJobTitle ? null : formikRef.current?.values.title,
              company: res.hasCompanyName ? null : formikRef.current?.values.company,
              employees: res.hasCompanyEmployeeCount ? null : formikRef.current?.values.employees,
            };
            await handleSub(zoomInfoValues, getWebEnrichedFields(res), res.contactToken);
            return;
          }
          if (res && res.success !== false) {
            setZoomInfoData(res);
          }
        }
        setStep(2);
        postTrialInterestBoomiService({
          businessMax,
          email: formEmail,
          contactPermission: contactPermission,
          trialSku: data.sku,
          programName: data.inputs?.programName?.defaultValue,
          programID: data.inputs?.programID?.defaultValue,
        });
        await postSegmentService({
          event: `${getEventName(true)}_trial_form_shown`,
          properties: {
            reason: getEventReason(),
            journey_step: 'Step 2',
          },
        });
      }
    } catch (error) {
      if (signal.aborted) return;
      setErrorMessage(data.globalError);
      trackError({
        code: '500',
        message: `Trial submission: ${error.message}`,
        runtime: true,
      });
    } finally {
      if (!signal.aborted) setFormSubmitting(false);
    }
  };

  const resetStep2State = () => {
    if (zoomInfoData) {
      setZoomInfoData(null);
    }
    formikRef.current?.setValues(
      {
        ...formikRef.current.values,
        fullName: '',
        title: '',
        company: '',
        employees: '',
        temp_phone__c: '',
      },
      false
    );
  };

  const handleBackClick = async () => {
    if (onBackOverride) {
      onBackOverride();
      return;
    }
    await postSegmentService({
      event: `${getEventName(true)}_trial_form_shown`,
      properties: {
        reason: getEventReason(),
        journey_step: 'Step 1',
      },
    });
    setStep(1);
    resetStep2State();
  };

  // let the modal know which step its on
  useEffect(() => {
    if (handleCallback) {
      handleCallback(step);
    }
  }, [step]);

  return (
    <>
      {modifier !== MODAL_TYPES.PASSWORDGEN && modifier !== MODIFIER_TYPE.HERO && (
        <h2 className={styles.header}>{step === 1 ? data.title : data.title2}</h2>
      )}
      <AlertBanner closedBanner={formError !== null || isBannerClosed} setClosedBanner={setClosedBanner} type={'error'}>
        <ConvertStringToReact htmlString={formError || data.globalError} errorCode={formErrorCode} />
      </AlertBanner>
      {isCompany && trialAllowed && (
        <p className={styles.differentEmail}>
          <ConvertStringToReact htmlString={data.useDifferentEmail.replace('{email}', `<b>${existingUserEmail}</b>`)} />
        </p>
      )}
      {step !== 1 || isModalModifier(modifier) || modifier === MODIFIER_TYPE.HERO ? (
        <></>
      ) : existingUserEmail ? (
        <div className={styles.user}>
          <div className={styles.userInfo}>
            <div className={styles.userIcon}>
              <UserIcon />
            </div>{' '}
            {existingUserEmail}
          </div>
          <a href={data.logoutLink}>{data.logoutText}</a>
        </div>
      ) : (
        <div className={formType === FORM_TYPES.DIRECTBUY ? styles.loginInline : styles.login}>
          <ConvertStringToReact htmlString={data.footerText} />
        </div>
      )}
      {trialAllowed && (
        <Formik<SendAsyncPostObject>
          validateOnMount={true}
          validateOnChange={true}
          validateOnBlur={true}
          innerRef={formikRef}
          initialValues={initialValues}
          onSubmit={async (values) => {
            const submissionValues: SendAsyncPostObject = zoomInfoData
              ? {
                  ...values,
                  fullName: zoomInfoData.hasFirstName && zoomInfoData.hasLastName ? null : values.fullName,
                  title: zoomInfoData.hasJobTitle ? null : values.title,
                  company: zoomInfoData.hasCompanyName ? null : values.company,
                  employees: zoomInfoData.hasCompanyEmployeeCount ? null : values.employees,
                }
              : values;
            await handleSub(submissionValues);
          }}
        >
          {({ errors, touched, values, isSubmitting, isValidating, isValid, submitCount }) => {
            return (
              <Form>
                <FormikEffect
                  submitCount={submitCount}
                  isValidating={isValidating}
                  isValid={isValid}
                  errors={errors}
                  isSubmitting={isSubmitting}
                  setClosedBanner={setClosedBanner}
                  formikRef={formikRef}
                  successUrl={data.successUrl}
                  trialLength={trialLength}
                  emailStep={step === 1 && is2Step}
                />
                <div
                  className={`${modifier === MODIFIER_TYPE.HERO && styles.heroWrap} ${modifier === MODIFIER_TYPE.HERO && step === 1 && styles.heroWrapStep1}`}
                >
                  {!trialAllowed || isCompany ? (
                    <></>
                  ) : directbuy ? (
                    <>
                      <BusinessBuyInput
                        formikTouched={touched}
                        formikErrors={errors}
                        formikValues={values}
                        inputs={data.inputs}
                        showHint={showHint}
                        trackErrorBlur={trackErrorBlur}
                        validateBusinessEmail={validateBusinessEmail}
                        existingAccount={existingAccount}
                        userEmail={existingUserEmail}
                        existingEmailError={data.existingEmailError}
                      />
                      {!existingUserEmail && <InfoBanner text={data.passwordInfo} />}
                    </>
                  ) : trialType === TRIAL_TYPES.MSP ? (
                    <MSPInput
                      formikRef={formikRef}
                      formikTouched={touched}
                      formikErrors={errors}
                      formikValues={values}
                      inputs={data.inputs}
                      showHint={showHint}
                      trackErrorBlur={trackErrorBlur}
                      validateBusinessEmail={validateBusinessEmail}
                      existingAccount={existingAccount}
                      userEmail={existingUserEmail}
                      existingEmailError={data.existingEmailError}
                    />
                  ) : trialType === TRIAL_TYPES.TEAMS ||
                    trialType === TRIAL_TYPES.PREMIUM ||
                    trialType === TRIAL_TYPES.FAMILIES ? (
                    <EmailOnlyInput
                      formikRef={formikRef}
                      formikTouched={touched}
                      formikErrors={errors}
                      formikValues={values}
                      inputs={data.inputs}
                      showHint={showHint}
                      trackErrorBlur={trackErrorBlur}
                      validateBusinessEmail={validateBusinessEmail}
                      existingAccount={existingAccount}
                      userEmail={existingUserEmail}
                      existingEmailError={data.existingEmailError}
                      mistypedEmailError={mistypedEmail}
                    />
                  ) : (
                    <BusinessInput
                      formikTouched={touched}
                      formikErrors={errors}
                      inputs={data.inputs}
                      showHint={showHint}
                      trackErrorBlur={trackErrorBlur}
                      validateBusinessEmail={validateBusinessEmail}
                      existingAccount={existingAccount}
                      userEmail={existingUserEmail}
                      existingEmailError={data.existingEmailError}
                      step={step}
                      zoomInfoData={zoomInfoData}
                    />
                  )}
                  <TrialButtonSwitch
                    formSubmitting={formSubmitting}
                    data={data}
                    modalContent={modalContent}
                    handleCallback={handleCallback}
                    handleBackClick={handleBackClick}
                    step={step}
                    is2Step={is2Step}
                    goToNextStep={goToNextStep}
                  />
                </div>
                {step === 1 && (
                  <div className={styles.terms} style={{ marginTop: modifier === MODIFIER_TYPE.HERO ? '0' : '20px' }}>
                    {isCanadaUser ? (
                      <div className={styles.caTermsWrap}>
                        <span className={styles.checkbox}>
                          <Field
                            name="optOutOfEmails"
                            as={Checkbox}
                            checked={contactPermission}
                            value={contactPermission}
                            label={''}
                            id="optOutOfEmails"
                            onClick={() => {
                              setOptOutOfEmails();
                            }}
                          />
                        </span>
                        <p
                          style={{
                            textAlign: isModalModifier(modifier) || modifier === MODIFIER_TYPE.HERO ? 'left' : 'left',
                            fontSize: modifier === MODAL_TYPES.PASSWORDGEN && '12px',
                            lineHeight: modifier === MODAL_TYPES.PASSWORDGEN && '16px',
                          }}
                          onClick={unsubscribeClick}
                        >
                          <ConvertStringToReact
                            htmlString={isCanadaUser && data!.canada_Terms ? data!.canada_Terms : data!.terms}
                          />
                        </p>
                      </div>
                    ) : (
                      <div>
                        <p
                          style={{
                            textAlign: isModalModifier(modifier) || modifier === MODIFIER_TYPE.HERO ? 'left' : 'center',
                            fontSize: modifier === MODAL_TYPES.PASSWORDGEN && '12px',
                            lineHeight: modifier === MODAL_TYPES.PASSWORDGEN && '16px',
                          }}
                          onClick={unsubscribeClick}
                        >
                          <ConvertStringToReact
                            htmlString={isCanadaUser && data!.canada_Terms ? data!.canada_Terms : data!.terms}
                          />
                        </p>
                        {displayUnsubscribeCheckbox && (
                          <div className={styles.checkboxBelow}>
                            <Field
                              name="optOutOfEmails"
                              as={Checkbox}
                              checked={!contactPermission}
                              value={!contactPermission}
                              label={data!.optOutOfEmails![0].label}
                              id="optOutOfEmails"
                              onClick={() => {
                                setOptOutOfEmails();
                              }}
                              className={styles.checkboxBelow}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </Form>
            );
          }}
        </Formik>
      )}
    </>
  );
};
